/** @jsx isolateComponent */

import { ReactNode, useState } from 'react';

import { AsyncOverlay, Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';

import {
  IntroPricingOfferModalView,
  ReducedTaskCompanyOfferModalView,
  ReducedTaskTeamOfferModalView,
} from '@zapier/promotions/src/components/campaigns';

import { campaignSlugs } from '@zapier/promotions/src/utils';

import { useOfferModal as _useOfferModal, UseOfferModalProps } from './hooks';

import {
  MultiIntervalOfferModalView,
  OfferAcceptedView,
  OfferErrorView,
  SingleIntervalOfferModalView,
} from './views';

type Props = {
  /** If not provided, the modal will try to grab an offerId from the current URL. */
  offerId?: string | null | undefined;
  /** Runs when the modal is closed without the user accepting the offer. */
  onClose?: () => void;
  /** For dependency injection testing only */
  useOfferModal?: ({
    offerId,
    onAcceptOfferSuccess,
  }: UseOfferModalProps) => ReturnType<typeof _useOfferModal>;
};

const getFullStoryEventName = (campaignName: string | null | undefined) => {
  let name = 'Viewed Offer Modal';

  if (campaignName) {
    name += ` - ${campaignName}`;
  }

  return name;
};

type ViewType = 'OFFER' | 'OFFER_ACCEPTED';

type ViewMap = {
  [x in ViewType]: ReactNode;
};

export const OfferModal = ({
  offerId,
  onClose = () => {},
  useOfferModal = _useOfferModal,
}: Props) => {
  const [activeView, setActiveView] = useState<ViewType>('OFFER');

  const onAcceptOfferSuccess = () => setActiveView('OFFER_ACCEPTED');

  const {
    acceptOffer,
    allowIntervalSelection,
    error,
    isLoading,
    isOfferAvailable,
    isPaymentMethodProvided,
    isSaving,
    offer,
    offerEndDate,
    promoPlans,
    showModal,
  } = useOfferModal({ offerId, onAcceptOfferSuccess });

  const [isOpen, setIsOpen] = useState(true);

  const closeModal = () => {
    setIsOpen(false);
    onClose();
  };

  const campaignSlug = offer?.variant?.campaign?.slug;

  if (!showModal || !isOpen || isLoading) {
    return null;
  }

  const wrapContent = (content: ReactNode) => {
    const props = {
      acceptOffer,
      error,
      isPaymentMethodProvided,
    };

    if (allowIntervalSelection) {
      const monthlyPlan = promoPlans.find(plan => plan.interval === 'month');
      const yearlyPlan = promoPlans.find(plan => plan.interval === 'year');

      return (
        monthlyPlan &&
        yearlyPlan && (
          <MultiIntervalOfferModalView
            {...props}
            monthlyIntervalAmount={monthlyPlan.amount}
            monthlyPlanId={monthlyPlan.id}
            yearlyIntervalAmount={yearlyPlan.amount}
            yearlyPlanId={yearlyPlan.id}
          >
            {content}
          </MultiIntervalOfferModalView>
        )
      );
    }

    return (
      <SingleIntervalOfferModalView {...props}>
        {content}
      </SingleIntervalOfferModalView>
    );
  };

  const getContent = (): ReactNode => {
    const tasks = (promoPlans && promoPlans[0]?.tasks) ?? 0;

    switch (campaignSlug) {
      case campaignSlugs.INTRO_STARTER: {
        const destinationPlan = offer?.variant?.destinationPlan;

        if (!offer || !destinationPlan || !offerEndDate) {
          break;
        }

        return wrapContent(
          <IntroPricingOfferModalView
            destinationPlanAmount={destinationPlan?.amount}
            destinationPlanName={
              destinationPlan?.displayShortName ?? destinationPlan?.name
            }
            maxUses={offer?.variant?.campaign?.maxUses}
            offerEndDate={offerEndDate}
            totalCost={offer?.variant?.totalCost}
          />
        );
      }

      case campaignSlugs.TEAM_REDUCED_TASK:
        return wrapContent(<ReducedTaskTeamOfferModalView tasks={tasks} />);

      case campaignSlugs.COMPANY_REDUCED_TASK:
        return wrapContent(<ReducedTaskCompanyOfferModalView tasks={tasks} />);
    }

    /**
     * If we get here, it means we haven't added the campaign's slug to this map,
     * there's a typo in the slug, or the campaign isn't configured correctly.
     */
    return (
      <OfferErrorView
        body="There was a problem finding details for this offer."
        onClose={closeModal}
        title="Uh oh"
      />
    );
  };

  const views: ViewMap = {
    OFFER: getContent(),
    OFFER_ACCEPTED: (
      <OfferAcceptedView
        planName={
          offer?.variant?.destinationPlan?.name ?? // Try single-interval promo
          (promoPlans && promoPlans[0]?.name) ?? // Try multi-interval promo
          ''
        }
      />
    ),
  };

  const isOfferAcceptedView = activeView === 'OFFER_ACCEPTED';

  return (
    <AsyncOverlay isVisible={isSaving}>
      <Modal
        closeOnOutsideClick={!isOfferAcceptedView}
        fullStoryEventName={getFullStoryEventName(
          offer?.variant?.campaign?.name
        )}
        onClose={isOfferAcceptedView ? undefined : closeModal} // Force user to click a CTA button
        overflowY={isOfferAcceptedView ? 'hidden' : 'auto'} // Hidden on success page to prevent confetti overflow
      >
        {!isOfferAvailable ? (
          <OfferErrorView
            body="This offer either no longer exists or has expired."
            onClose={closeModal}
            title="Offer Not Found"
          />
        ) : (
          views[activeView]
        )}
      </Modal>
    </AsyncOverlay>
  );
};
